import { useEffect, useLayoutEffect, useState } from 'react';
import * as am5 from '@amcharts/amcharts5';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import * as am5xy from '@amcharts/amcharts5/xy';
import { Select } from 'antd';
import { useTheme } from 'react-admin';

import { reportsHttp } from '@network';
import { moneyFormatter } from '@utils';
import cls from './top-buyers-ranking.module.css';

interface Props {
  range: string;
  setLoader: (num: number, value: boolean) => void;
}

const countOptions = [
  {
    label: 'Top 5',
    value: 5,
  },
  {
    label: 'Top 10',
    value: 10,
  },
  {
    label: 'Top 20',
    value: 20,
  },
];

const filterOptions = [
  {
    label: 'Number of credits signed',
    value: 'count',
  },
  {
    label: 'Credit Amount of signed credits',
    value: 'amount',
  },
];

const exportToCSV = (data: any[], filename: string, filterType: 'count' | 'amount') => {
  if (!data || data.length === 0) {
    console.warn('No data to export');

    return;
  }

  const valueHeader = filterType === 'count' ? 'Number of credits signed' : 'Credit Amount of signed credits';
  const headers = ['#', 'Buyer ', valueHeader];

  const csvContent = [
    headers.join(','),
    ...data.map((item, index) => [
      index + 1,
      `"${item.name}"`,
      `"${filterType === 'count' ? item.value : moneyFormatter.format(item.value)}"`,
    ].join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};


export const TopBuyersRanking = ({ range, setLoader }: Props) => {
  const [numbers, setNumbers] = useState<any>([]);
  const [count, setCount] = useState(5);
  const [filterType, setFilterType] = useState<'count' | 'amount'>('count');
  const [currentTheme] = useTheme();
  const id = 'top-buyers-ranking';

  const loadNumbers = async () => {
    setLoader(7, true);
    try {
      const nextNumbers = await reportsHttp.topBuyersRanking({ range, count, filterType });
      setNumbers(nextNumbers);
    } catch (e) {
      console.error(e);
    } finally {
      setLoader(7, false);
    }
  };

  const handleExportCSV = () => {
    const filename = 'top-buyers-ranking.csv';
    exportToCSV(numbers, filename, filterType);
  };

  useEffect(() => void loadNumbers(), [range, count, filterType]);

  useLayoutEffect(() => {
    const root = am5.Root.new(id);
    root.setThemes([
      am5themes_Animated.new(root),
    ]);

    const labelColor = am5.color(currentTheme === 'light' ? '#000000' : '#ffffff');

    const chart = root.container.children.push(am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      paddingLeft: 0,
      wheelX: 'panX',
      wheelY: 'zoomX',
      layout: root.verticalLayout,
    }));

    const processedNumbers = numbers.map((item: any) => ({
      ...item,
      displayName: item.name && item.name.length > 20
        ? `${item.name.substring(0, 20)}...`
        : item.name,
      originalName: item.name,
    }));

    const xRenderer = am5xy.AxisRendererX.new(root, {
      cellStartLocation: 0,
      cellEndLocation: 1,
      minorGridEnabled: true,
    } as any);

    const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: 'displayName',
      renderer: xRenderer,
      tooltip: am5.Tooltip.new(root, {}),
    }));

    xRenderer.grid.template.setAll({ location: 1 });

    const labelSettings = {
      oversizedBehavior: 'wrap' as const,
      textAlign: 'center' as const,
      fontSize: 12,
      paddingLeft: 10,
      paddingRight: 10,
      rotation: -90,
      fill: labelColor,
    };

    xAxis.get('renderer').labels.template.setAll(labelSettings);
    xAxis.data.setAll(processedNumbers);

    const yRenderer = am5xy.AxisRendererY.new(root, {
      strokeOpacity: 0.1,
    });

    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      renderer: yRenderer,
      strictMinMax: true,
      min: 0,
    }));
    yAxis.get('renderer').labels.template.setAll({ fill: labelColor });

    const series = chart.series.push(am5xy.ColumnSeries.new(root, {
      name: 'Top Buyers - Ranking',
      xAxis: xAxis,
      yAxis: yAxis,
      valueYField: 'value',
      categoryXField: 'displayName',
      fill: am5.color('#FD8A32'),
      stroke: am5.color('#FD8A32'),
    }));

    const tooltip = am5.Tooltip.new(root, {
      getFillFromSprite: false,
      fill: am5.color('#042E6B'),
      stroke: am5.color('#042E6B'),
    }) as any;

    tooltip.get('background')?.setAll({
      fill: am5.color('#ffffff'),
      stroke: am5.color('#ffffff'),
    });

    tooltip.label.setAll({
      fill: am5.color('#ffffff'),
    });

    series.columns.template.setAll({
      tooltipText: filterType === 'amount'
        ? '{originalName}: ${valueY.formatNumber("#,###")}'
        : '{originalName}: {valueY}',
      width: am5.percent(80),
      tooltipY: 0,
      strokeOpacity: 0,
      tooltip: tooltip,
    });

    series.data.setAll(processedNumbers);

    series.appear();

    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [id, numbers, filterType, count, currentTheme]);

  const height = numbers && numbers.length > 0 ? 650 : 50;

  return (
    <div className={cls.wrap}>
      <div className={cls.head}>
        <div className={cls.title}>Top Buyers - Ranking</div>
        <div className={cls.options}>
          <Select
            className={cls.select}
            options={countOptions}
            onSelect={setCount}
            value={count}
          />
          <Select
            className={cls.select}
            options={filterOptions}
            onSelect={setFilterType}
            value={filterType}
          />
        </div>
        <div className={cls.csv} onClick={handleExportCSV}>
          <svg xmlns="http://www.w3.org/2000/svg" width="37" height="28" viewBox="0 0 37 28" fill="none">
            <g filter="url(#filter0_d_4017_15730)">
              <rect x="4" width="28.7805" height="20" rx="5" fill="#042E6B"/>
            </g>
            <path d="M17.2834 4.72766C17.2834 4.32579 17.6224 4 18.0407 4C18.4589 4 18.798 4.32579 18.798 4.72766V12.7319C18.798 13.1338 18.4589 13.4596 18.0407 13.4596C17.6224 13.4596 17.2834 13.1338 17.2834 12.7319V4.72766Z" fill="white"/>
            <path d="M14.2218 10.6997C13.9261 10.4155 13.9261 9.95476 14.2218 9.67059C14.5175 9.38642 14.997 9.38642 15.2928 9.67059L18.5056 12.7578C18.8013 13.042 18.8013 13.5027 18.5056 13.7869C18.2099 14.071 17.7304 14.071 17.4347 13.7869L14.2218 10.6997Z" fill="white"/>
            <path d="M21.7782 10.6997C22.0739 10.4155 22.0739 9.95476 21.7782 9.67059C21.4825 9.38642 21.003 9.38642 20.7072 9.67059L17.4944 12.7578C17.1987 13.042 17.1987 13.5027 17.4944 13.7869C17.7901 14.071 18.2696 14.071 18.5653 13.7869L21.7782 10.6997Z" fill="white"/>
            <rect x="10" y="15" width="17" height="2" rx="1" fill="white"/>
            <rect x="12" y="10" width="7" height="2" rx="1" transform="rotate(90 12 10)" fill="white"/>
            <rect x="27" y="10" width="7" height="2" rx="1" transform="rotate(90 27 10)" fill="white"/>
            <defs>
              <filter id="filter0_d_4017_15730" x="0" y="0" width="36.7805" height="28" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="4"/>
                <feGaussianBlur stdDeviation="2"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_4017_15730"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_4017_15730" result="shape"/>
              </filter>
            </defs>
          </svg>
        </div>
      </div>
      <div
        id={id}
        className={cls.chart}
        style={{ height, marginBottom: 40 }}
      />
    </div>
  );
};
